"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.YoutubeVideosService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@wix/wix-vod-constants/common");
const api_types_1 = require("@wix/wix-vod-constants/api-types");
const to_error_1 = require("../../parsers/to-error");
const video_1 = require("./parsers/video");
const video_item_1 = require("../../parsers/video-item");
const youtube_channels_service_1 = require("./youtube-channels-service");
const youtube_1 = require("@wix/wix-vod-api-site-based/dist/src/public/services/youtube/youtube");
class YoutubeVideosService {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this.youtubeService = new youtube_1.YoutubeService(httpClient);
        this.youtubeChannelsService = new youtube_channels_service_1.YoutubeChannelsService(httpClient);
    }
    mapParams(channel, options) {
        const { externalId, urlType } = channel;
        const item = {
            externalId,
            urlType,
            pageToken: options.paging.cursor,
            itemsCount: options.paging.size,
        };
        return item;
    }
    getCategoryIdToTitleMapping(categoryItems) {
        return _.reduce(categoryItems, (categoriesByIds, category) => ({
            [category.id]: category.snippet.title,
        }), {});
    }
    addCategory(categoriesByIds, item) {
        const categoryId = _.get(item, 'snippet.categoryId');
        const category = categoriesByIds[categoryId];
        if (category) {
            _.set(item, 'snippet.category', category);
        }
        return item;
    }
    mapToVodPaginatedResponse(response, options) {
        const { nextPageToken, playlistItems, videoItems, categoryItems, channelItems, } = response.data;
        const categoriesByIds = this.getCategoryIdToTitleMapping(categoryItems);
        const channelsData = _.keyBy(channelItems, 'id');
        const addCategoryToVideo = (item) => this.addCategory(categoriesByIds, item);
        const mergedItems = videoItems.map((videoItem, index) => _.merge(videoItem, playlistItems[index]));
        return {
            status: response.status,
            sort: options.sort,
            paging: Object.assign(Object.assign({}, options.paging), { cursor: nextPageToken }),
            data: _(mergedItems)
                .map(addCategoryToVideo)
                .map((item) => {
                var _a, _b;
                return (Object.assign(Object.assign({}, (0, video_1.parseYoutubeVideo)(item)), { publisher: (_b = (_a = channelsData[item.snippet.channelId]) === null || _a === void 0 ? void 0 : _a.snippet) === null || _b === void 0 ? void 0 : _b.title }));
            })
                .value(),
        };
    }
    listYoutubeChannelVideosInternal(channel, options, hydratedChannelVideos) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const params = this.mapParams(channel, options);
            const response = hydratedChannelVideos ||
                (yield this.youtubeService.getChannelVideos(params));
            return this.mapToVodPaginatedResponse(response, options);
        });
    }
    getVideosByChannelId(channelId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.listYoutubeChannelVideosInternal({ externalId: channelId, urlType: common_1.URL_TYPES.CHANNEL }, options).catch(to_error_1.toError);
        });
    }
    getVideosByPlaylistId(playlistId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.listYoutubeChannelVideosInternal({ externalId: playlistId, urlType: common_1.URL_TYPES.PLAYLIST }, options).catch(to_error_1.toError);
        });
    }
    getVideosByUserName(userName, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.youtubeService
                .getChannelsByUsername(userName)
                .then((response) => response.data.items[0])
                .then((channel) => channel.id)
                .then((channelId) => this.getVideosByChannelId(channelId, options))
                .catch(to_error_1.toError);
        });
    }
    getYoutubeVideoByIdWithChannelData(videoId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const videoResponse = yield this.getYoutubeVideoById(videoId);
            const channel = yield this.youtubeChannelsService
                .getChannelsByIds([videoResponse.data.channelId])
                .then((response) => response.data[0]);
            return Object.assign(Object.assign({}, videoResponse), { data: Object.assign(Object.assign({}, videoResponse.data), { publisher: channel ? channel.title : null }) });
        });
    }
    getVideosByUrl(url, skipVideos) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            let type;
            let id;
            try {
                const result = skipVideos
                    ? this.youtubeService.parseChannelUrl(url)
                    : this.youtubeService.parseVideoUrl(url);
                type = result.type;
                id = result.id;
            }
            catch (e) {
                return Promise.reject(new api_types_1.InternalError(0, 'wpm_error.malformed_external_link'));
            }
            const options = {
                paging: {
                    cursor: null,
                    size: 50,
                },
                sort: {
                    order: api_types_1.SortOrders.Relevance,
                },
            };
            switch (type) {
                case common_1.URL_TYPES.CHANNEL:
                    return this.getVideosByChannelId(id, options);
                case common_1.URL_TYPES.PLAYLIST:
                    return this.getVideosByPlaylistId(id, options);
                case common_1.URL_TYPES.USER_NAME:
                    return this.getVideosByUserName(id, options);
                default:
                    try {
                        const response = yield this.getYoutubeVideoByIdWithChannelData(id);
                        return Object.assign(Object.assign({}, response), { data: [response.data] });
                    }
                    catch (error) {
                        return (0, to_error_1.toError)(error);
                    }
                // .catch(toError);
            }
        });
    }
    getYoutubeVideoById(videoId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.youtubeService.getVideoById(videoId);
            const { items, categoryItems } = response.data;
            const categoriesByIds = this.getCategoryIdToTitleMapping(categoryItems);
            const item = this.addCategory(categoriesByIds, items[0]);
            const parsedItem = (0, video_1.parseYoutubeVideo)(item);
            return Object.assign(Object.assign({}, response), { data: Object.assign(Object.assign({}, parsedItem), { id: parsedItem.externalId }) });
        });
    }
    listYoutubeChannelVideos(channel, options, hydratedChannelVideos) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this.listYoutubeChannelVideosInternal(channel, options, hydratedChannelVideos);
            return Object.assign(Object.assign({}, response), { data: response.data.map(video_item_1.parseVideoItem) });
        });
    }
}
exports.YoutubeVideosService = YoutubeVideosService;
//# sourceMappingURL=youtube-videos-service.js.map